import './index.scss'

import { useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import ServicesController from '@data/controller/ServicesController'
import { ChurchService } from '@interfaces/service.interface'

import closeIcon from 'url:@icon/close.svg';
import warningIcon from 'url:@icon/error-icon.svg';

const HTTP_NOT_FOUND = 404;

// Styled after the Launchpad and Settings delete modals.
export default function DeleteServiceModal(props: {
  isOpen: boolean,
  service: ChurchService,
  close: () => void,
  onDeleted: () => void
}) {
  const auth = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = props.service.attributes.title || 'this service';

  const deleteService = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await ServicesController.deleteService(auth.churchId, props.service.id, auth);

      // Already gone, most likely deleted elsewhere: the outcome asked for.
      if (response.error && response.status !== HTTP_NOT_FOUND) {
        setError(response.error);
        return;
      }

      props.onDeleted();
    } finally {
      setIsDeleting(false);
    }
  };

  return <Modal
    id='delete-service-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: isDeleting ? 'Deleting...' : 'Delete Service',
        style: ModalButtonStyle.FILLED,
        enabled: !isDeleting,
        onClick: () => {
          void deleteService();
        }
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isDeleting,
        onClick: props.close
      }
    ]}>
    <div className='delete-service-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='close'
          disabled={isDeleting}
          onClick={props.close}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>Delete Service</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <img src={warningIcon} alt='' aria-hidden='true' className='warning-icon' />

        <p className='warning-copy'>
          <strong>Are you sure you want to delete {title}?</strong>
        </p>

        <p className='warning-copy'>
          It will be removed from Launchpad and the Salt app, along with its songs, scripture, and discussion
          questions. This action cannot be undone.
        </p>
      </div>
    </div>
  </Modal>
}
